import { memo, useState } from 'react';
import { useRouter } from 'next/router';

import { Checkbox, FieldSet, Label, LabeledControl, Legend, Toast, useToast } from '@zillow/constellation-10';

import {
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasBusinessCenterCommunityRoom,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasClubHouseCommunityRoom,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasConferenceRoomCommunityRoom,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasFitnessCenterCommunityRoom,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasGameRoomCommunityRoom,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasLibraryCommunityRoom,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasLoungeCommunityRoom,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasPartyRoomCommunityRoom,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasPetWashingStationCommunityRoom,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasTheaterCommunityRoom,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';
import { DatarouterError } from '@zg-rentals/web-base';

import { useGetListingDetails } from '../../../../hooks/queries/properties';
import { useBatchMutationToast } from '../../../../hooks/useBatchMutationToast';
import { usePropertyEditMutation } from '../../../../hooks/usePropertyEditMutation';
import { type AmenityFlagSave } from '../../utils';
import { FeedOverrideSection } from '@zg-rentals/ts-rental-manager-api-client';

import { useFeedAmenityMutation } from '../../../../hooks/useFeedAmenityMutation';
import { useIsInputFormDisabled } from '../../../../hooks/useIsInputFormDisabled';

import {
  COMMUNITY_ROOM_KEYS,
  COMMUNITY_ROOM_METADATA,
  type CommunityRoomKey,
} from '../../../../constants/KeyFeaturesConstants';

import { useAuth } from '../../../AuthProvider';

type LocalState = Record<CommunityRoomKey, boolean>;

const CommunityRoomsAmenities = (): JSX.Element | null => {
  const router = useRouter();
  const { user } = useAuth();
  const { enqueueToast } = useToast();

  const listingAlias = router.query.listingAlias as string;
  const { data: listingDetails } = useGetListingDetails(listingAlias);

  const communityRoomTypes = listingDetails?.buildingAttributes?.communityRoomTypes;
  const canUpdateListing = user?.features?.canUpdateListing ?? false;

  const [localState, setLocalState] = useState<LocalState>({
    hasBusinessCenter: communityRoomTypes?.hasBusinessCenter ?? false,
    hasClubHouse: communityRoomTypes?.hasClubHouse ?? false,
    hasConferenceRoom: communityRoomTypes?.hasConferenceRoom ?? false,
    hasFitnessCenter: communityRoomTypes?.hasFitnessCenter ?? false,
    hasGameRoom: communityRoomTypes?.hasGameRoom ?? false,
    hasLibrary: communityRoomTypes?.hasLibrary ?? false,
    hasLounge: communityRoomTypes?.hasLounge ?? false,
    hasPartyRoom: communityRoomTypes?.hasPartyRoom ?? false,
    hasPetWashingStation: communityRoomTypes?.hasPetWashingStation ?? false,
    hasTheater: communityRoomTypes?.hasTheater ?? false,
  });

  const { startRequest, handleSuccess, handleError } = useBatchMutationToast({
    successMessage: 'Community rooms updated',
  });

  const isInputFormDisabled = useIsInputFormDisabled(listingDetails, canUpdateListing, FeedOverrideSection.AMENITIES);
  const { isFeedListing, setFeedAmenity } = useFeedAmenityMutation();

  const { mutateAsync: updateBusinessCenter } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasBusinessCenterCommunityRoom,
    skipToast: true,
    meta: {
      context: {
        api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasBusinessCenterCommunityRoom',
        details: { listingAlias },
      },
    },
  });

  const { mutateAsync: updateClubHouse } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasClubHouseCommunityRoom,
    skipToast: true,
    meta: {
      context: {
        api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasClubHouseCommunityRoom',
        details: { listingAlias },
      },
    },
  });

  const { mutateAsync: updateConferenceRoom } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasConferenceRoomCommunityRoom,
    skipToast: true,
    meta: {
      context: {
        api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasConferenceRoomCommunityRoom',
        details: { listingAlias },
      },
    },
  });

  const { mutateAsync: updateFitnessCenter } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasFitnessCenterCommunityRoom,
    skipToast: true,
    meta: {
      context: {
        api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasFitnessCenterCommunityRoom',
        details: { listingAlias },
      },
    },
  });

  const { mutateAsync: updateGameRoom } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasGameRoomCommunityRoom,
    skipToast: true,
    meta: {
      context: {
        api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasGameRoomCommunityRoom',
        details: { listingAlias },
      },
    },
  });

  const { mutateAsync: updateLibrary } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasLibraryCommunityRoom,
    skipToast: true,
    meta: {
      context: {
        api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasLibraryCommunityRoom',
        details: { listingAlias },
      },
    },
  });

  const { mutateAsync: updateLounge } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasLoungeCommunityRoom,
    skipToast: true,
    meta: {
      context: {
        api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasLoungeCommunityRoom',
        details: { listingAlias },
      },
    },
  });

  const { mutateAsync: updatePartyRoom } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasPartyRoomCommunityRoom,
    skipToast: true,
    meta: {
      context: {
        api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasPartyRoomCommunityRoom',
        details: { listingAlias },
      },
    },
  });

  const { mutateAsync: updatePetWashingStation } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasPetWashingStationCommunityRoom,
    skipToast: true,
    meta: {
      context: {
        api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasPetWashingStationCommunityRoom',
        details: { listingAlias },
      },
    },
  });

  const { mutateAsync: updateTheater } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasTheaterCommunityRoom,
    skipToast: true,
    meta: {
      context: {
        api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasTheaterCommunityRoom',
        details: { listingAlias },
      },
    },
  });

  const mutationMap = {
    hasBusinessCenter: updateBusinessCenter,
    hasClubHouse: updateClubHouse,
    hasConferenceRoom: updateConferenceRoom,
    hasFitnessCenter: updateFitnessCenter,
    hasGameRoom: updateGameRoom,
    hasLibrary: updateLibrary,
    hasLounge: updateLounge,
    hasPartyRoom: updatePartyRoom,
    hasPetWashingStation: updatePetWashingStation,
    hasTheater: updateTheater,
  } as unknown as Record<keyof LocalState, AmenityFlagSave>;

  const handleCheckboxChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const fieldName = event.target.name as CommunityRoomKey;
    const checked = event.target.value === 'true';
    const previousValue = !checked;

    startRequest();
    setLocalState((prev) => ({ ...prev, [fieldName]: checked }));

    try {
      const metadata = COMMUNITY_ROOM_METADATA[fieldName];
      if (isFeedListing) {
        await setFeedAmenity(metadata.apiKey, checked);
      } else {
        await mutationMap[fieldName]({
          postData: {
            [metadata.apiKey]: checked,
            listingAlias,
          },
        });
      }
      handleSuccess();
    } catch (err) {
      handleError();
      setLocalState((prev) => ({ ...prev, [fieldName]: previousValue }));
      const errorMessage = DatarouterError.getDisplayMessage(
        err,
        'Something went wrong, refresh the page and try again',
      );
      enqueueToast(<Toast tone="critical">{errorMessage}</Toast>);
    }
  };

  return (
    <FieldSet legend={<Legend>Community rooms</Legend>} disabled={isInputFormDisabled}>
      {COMMUNITY_ROOM_KEYS.map((fieldName) => {
        const metadata = COMMUNITY_ROOM_METADATA[fieldName];
        return (
          <LabeledControl
            key={`communityRoomTypes-${metadata.label}`}
            label={<Label>{metadata.label}</Label>}
            control={
              <Checkbox
                name={fieldName}
                value={String(!localState[fieldName])}
                checked={localState[fieldName]}
                onChange={handleCheckboxChange}
              />
            }
          />
        );
      })}
    </FieldSet>
  );
};

export default memo(CommunityRoomsAmenities);
